import { formatMisUpstreamError } from './misUpstreamError'
import { useEffect, useMemo, useState } from "react"
import type { TenantBootstrapModel } from "@unified-lk/tenant-config"
import type { AuthSession } from "../../../../types/plans"
import { buildCardWizardPreambleSteps } from "./diagnocatCardWizard"
import { pickDefaultVatechStudy } from "./domain/vatechCtCatalog"
import { enrichVatechStudiesWithDiagnocat } from "./domain/enrichVatechStudiesWithDiagnocat"
import { usePatientDiagnosticsLinked } from "./usePatientDiagnosticsLinked"
import { VatechCtCatalogTable } from "./VatechCtCatalogTable"
import { DiagnocatLinkPrompt } from "./DiagnocatLinkPrompt"
import { DiagnocatCtToPlanPanel } from "./DiagnocatCtToPlanPanel"
import { WorkflowStepProgress } from "./WorkflowStepProgress"
import { ctArchiveLabel, ctArchivePcSuffix } from "./domain/ctArchiveLabel"
import type { ConstructorPlan } from "../constructorTypes"

type DiagnosticsLinkSnapshot = ReturnType<typeof usePatientDiagnosticsLinked>

type DiagnocatPatientCardWizardProps = {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  lkPatientId: number | null
  misPatientId?: string | null
  staffUserId: string
  enabled?: boolean
  linkSnapshot?: DiagnosticsLinkSnapshot
  plan?: ConstructorPlan | null
  onApplied?: () => void
  onMisPlansRefresh?: (misPlanId: string | null) => void
  onAnalysisReady?: () => void
}

export function DiagnocatPatientCardWizard({
  bootstrap,
  session,
  lkPatientId,
  misPatientId = null,
  staffUserId,
  enabled = true,
  linkSnapshot,
  plan = null,
  onApplied,
  onMisPlansRefresh,
  onAnalysisReady,
}: DiagnocatPatientCardWizardProps) {
  const [selectedStudyUid, setSelectedStudyUid] = useState("")

  const internalLink = usePatientDiagnosticsLinked({
    bootstrap,
    session,
    lkPatientId,
    misPatientId,
    enabled: enabled && Boolean(lkPatientId) && !linkSnapshot,
  })

  const {
    diagnosticsLinked,
    diagnocatData,
    diagnocatLoading,
    catalogHook,
    reloadDiagnocat,
  } = linkSnapshot ?? internalLink

  const resolvedMisPatientId = misPatientId ?? catalogHook.catalog?.mis_patient_id ?? null
  const studies = useMemo(
    () => enrichVatechStudiesWithDiagnocat(catalogHook.studies, diagnocatData?.studies),
    [catalogHook.studies, diagnocatData?.studies],
  )
  const legacyPatientId = diagnocatData?.legacy_patient_id ?? lkPatientId


  useEffect(() => {
    if (!enabled || !lkPatientId) return
    const hasReady = studies.some(
      (s) => s.diagnocat_analysis_ready || (s.diagnocat_analyses_count ?? 0) > 0,
    )
    if (!hasReady) return
    if ((diagnocatData?.total_analyses ?? 0) > 0) return
    void reloadDiagnocat()
  }, [enabled, lkPatientId, studies, diagnocatData?.total_analyses, reloadDiagnocat])

  useEffect(() => {
    if (selectedStudyUid || studies.length === 0) return
    const picked = pickDefaultVatechStudy(studies)
    if (picked?.study_uid) setSelectedStudyUid(picked.study_uid)
  }, [selectedStudyUid, studies])

  const candidates = diagnocatData?.ambiguous_matches ?? []
  const tenantId = bootstrap.tenant
  const linkStatus =
    diagnosticsLinked ? "linked" : (diagnocatData?.status ?? catalogHook.catalog?.patient_linked ? "linked_via_mis" : "unknown")
  const preambleSteps = buildCardWizardPreambleSteps({
    tenantId,
    catalog: catalogHook.catalog,
    catalogLoading: catalogHook.isLoading,
    catalogError: catalogHook.isError ? String(catalogHook.error) : null,
    linkState: {
      linked: diagnosticsLinked,
      status: linkStatus,
      legacyPatientId,
    },
    hasLinkCandidates: candidates.length > 0,
  })

  if (!enabled || !lkPatientId) return null

  return (
    <section className="grid gap-4 rounded-[28px] border border-sky-200/80 bg-sky-50/30 p-5 shadow-sm">
      <div>
        <h3 className="text-lg font-semibold text-slate-900">КТ на {ctArchiveLabel(tenantId)} → Diagnocat → план</h3>
        <p className="mt-1 text-sm text-slate-600">
          Справочник КТ с {ctArchivePcSuffix(tenantId)}, связка Diagnocat и wizard до черновика плана.
        </p>
      </div>

      <WorkflowStepProgress steps={preambleSteps} ariaLabel="Подготовка wizard" />

      {catalogHook.isLoading ? (
        <p className="text-sm text-slate-500">Загружаем справочник КТ…</p>
      ) : catalogHook.isError ? (
        <p className="text-sm text-amber-700">Не удалось загрузить каталог: {formatMisUpstreamError(catalogHook.error)}</p>
      ) : studies.length === 0 ? (
        <p className="text-sm text-slate-500">КТ не найдено на {ctArchivePcSuffix(tenantId)} по ФИО и ID.</p>
      ) : (
        <VatechCtCatalogTable
          studies={studies}
          selectedStudyUid={selectedStudyUid}
          onSelectStudy={setSelectedStudyUid}
          resolveStrategy={catalogHook.catalog?.resolve_strategy}
        />
      )}

      {diagnocatLoading ? (
        <p className="text-sm text-slate-500">Проверяем связку Diagnocat…</p>
      ) : (
        <DiagnocatLinkPrompt
          bootstrap={bootstrap}
          session={session}
          lkPatientId={lkPatientId}
          legacyPatientId={legacyPatientId}
          linked={diagnosticsLinked}
          status={linkStatus}
          candidates={candidates}
          onLinked={() => {
            void reloadDiagnocat()
            void catalogHook.refetch()
          }}
        />
      )}

      <DiagnocatCtToPlanPanel
        bootstrap={bootstrap}
        session={session}
        plan={plan}
        lkPatientId={lkPatientId}
        misPatientId={resolvedMisPatientId}
        staffUserId={staffUserId}
        diagnosticsLinked={diagnosticsLinked}
        externalStudies={studies}
        externalDiagnocatData={diagnocatData}
        selectedStudyUid={selectedStudyUid}
        onSelectStudy={setSelectedStudyUid}
        hideCatalog
        onApplied={() => onApplied?.()}
        onMisPlansRefresh={onMisPlansRefresh}
        onAnalysisReady={() => {
          void reloadDiagnocat()
          onAnalysisReady?.()
        }}
      />
    </section>
  )
}
